import React, { FC, useContext, useEffect, useState } from "react";
import { Col, Form, Image, Row } from "react-bootstrap";
import PersonsSelect from "../../../../components/PersonsSelect";
import { PersonPhenotypeType, PersonType } from "../../../../types/PersonType";
import { Formik, useFormik } from "formik";
import * as yup from "yup";
import CustomSelect from "../../../../components/form/CustomSelect";
import { PersonModel } from "../../../../models/PersonModel";
import { OptionType } from "../../../../types/CommonType";
import ImageUploader from "../../../../components/ImageUploader";
import { ImageUploadProvider, ImageUploadProviderContext } from "../../../../providers/ImageUploadProvider";
import { useConfig } from "../../../../hooks/useConfig";
import InputMask from 'react-input-mask';
import clsx from "clsx";
import moment from "moment";
import StateList from "../../../../components/form/StateList";
import CityList from "../../../../components/form/CityList";
import { OptionsContext } from "../../../../providers/OptionsContextProvider";
import { FormattedMessage, useIntl } from "react-intl";
import { LANG_CITY, LANG_COMMON_CHOOSE, LANG_COMMON_MEDICAL_RECORD_NUMBER, LANG_COMMON_NAME, LANG_COMMON_ZIPCODE, LANG_DISTRICT, LANG_PERSON_AGE, LANG_PERSON_BIRTH_DATE, LANG_PERSON_BLOOD_TYPE, LANG_PERSON_BMI, LANG_PERSON_EYES_COLOR, LANG_PERSON_HAIR_COLOR, LANG_PERSON_HAIR_TYPE, LANG_PERSON_HEIGHT, LANG_PERSON_OTHER_INFO, LANG_PERSON_OTHER_INFO_DETAILS, LANG_PERSON_PICTURE, LANG_PERSON_PICTURE_KID, LANG_PERSON_RACE, LANG_PERSON_RH_FACTOR, LANG_PERSON_SKIN_COLOR, LANG_PERSON_WEIGHT, LANG_STATE } from "../../../../lang";
import Input from "react-select/dist/declarations/src/components/Input";

interface Props {
    person: PersonType
    personPhenotype?: PersonPhenotypeType
    onSubmit: (values: PersonPhenotypeType) => void
    onUpdate?: (person: PersonPhenotypeType) => void
    onValidationError?: () => void
    hasSubmitted: boolean
}

const PersonPhenotypeForm: FC<Props> = ({ person, personPhenotype, onSubmit, onUpdate = undefined, hasSubmitted, onValidationError = () => { } }) => {
    const intl = useIntl();
    const [imc, setImc] = useState("0.00");
    const options = useContext(OptionsContext);    

    const calculateBmi = (height: string | undefined, weight: string | undefined) => {
        if (weight !== undefined && height !== undefined)
            return (parseFloat(weight) / Math.pow(parseFloat(height), 2)).toFixed(2);

        return undefined
    }

    const updateImc = (height: string | undefined, weight: string | undefined) => {
        const bmi = calculateBmi(height, weight);
        if (bmi !== 'NaN' && bmi !== undefined) {
            setImc(bmi);
        }
    }

    const schema = yup.object().shape({
        skin_color: yup.string().required().nullable(),
        eyes_color: yup.string().required().nullable(),
        hair_color: yup.string().required().nullable(),
        hair_type: yup.string().required().nullable(),
        height: yup.number().required().nullable(),
        weight: yup.number().required().nullable(),
        blood_type: yup.string().required().nullable(),
        rh_factor: yup.string().required().nullable(),
        race: yup.string().required().nullable(),
        notes: yup.string().nullable(),
        zipcode: yup.string().required()
    });

    const formik = useFormik({
        initialValues: { id: personPhenotype?.id || 0, person_id: person.id, ...personPhenotype},
        validationSchema: schema,
        onSubmit: () => { },
        validateOnMount: false
    });

    useEffect(() => {
        updateImc(person.height_number, person.weight);
    }, [person]);

    useEffect(() => {
        if (onUpdate !== undefined)
            onUpdate(formik.values);
    }, [formik.values]);

    useEffect(() => {
        if (hasSubmitted) {
            for (let field in formik.values) {
                formik.setFieldTouched(field, true);
            }

            formik.validateForm(formik.values).then((errors) => {

                // Validate person height to maximum 2.5 meters
                if (formik.values.height_number !== undefined && parseFloat(formik.values.height_number) > 2.5) {
                    alert("Confira a altura da pessoa, ela não pode ser maior que 2.5 metros.");
                    return;
                }

                // Validate person weight to maximum 200 kg
                if (formik.values.weight !== undefined && parseFloat(formik.values.weight) > 200) {
                    alert("Confira o peso da pessoa, ele não pode ser maior que 200 kg.");
                    return;
                }

                if (Object.keys(errors).length === 0) {
                    onSubmit(formik.values)
                } else {
                    onValidationError();
                }
            });
        }
    }, [hasSubmitted]);

    return (
        <>
            <Row>
                <Col lg={3}>
                    <Form.Group className="mb-3">
                        <Form.Label>{intl.formatMessage(LANG_PERSON_BLOOD_TYPE)} <span className="text-danger">*</span></Form.Label>
                        <CustomSelect
                            name="blood_type"
                            options={PersonModel().getBloodTypes()}
                            onChange={(option: OptionType) => formik.setFieldValue("blood_type", option.value)}
                            value={formik.values.blood_type}
                            placeholder={`${intl.formatMessage(LANG_COMMON_CHOOSE)}`}
                            isSearchable={false}
                            isTouched={formik.touched.blood_type}
                            isValid={formik.touched.blood_type && !formik.errors.blood_type}
                        />
                    </Form.Group>
                </Col>
                <Col lg={3}>
                    <Form.Group className="mb-3">
                        <Form.Label>{intl.formatMessage(LANG_PERSON_RH_FACTOR)} <span className="text-danger">*</span></Form.Label>
                        <CustomSelect
                            name="rh_factor"
                            options={options.personOptions?.getRHFactorTypes()}
                            onChange={(option: OptionType) => formik.setFieldValue("rh_factor", option.value)}
                            value={formik.values.rh_factor}
                            placeholder={`${intl.formatMessage(LANG_COMMON_CHOOSE)}`}
                            isSearchable={false}
                            isTouched={formik.touched.rh_factor}
                            isValid={formik.touched.rh_factor && !formik.errors.rh_factor}
                        />
                    </Form.Group>
                </Col>
                <Col lg={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>{intl.formatMessage(LANG_PERSON_RACE)} <span className="text-danger">*</span></Form.Label>
                        <CustomSelect
                            name="race"
                            options={options.personOptions?.getRaceTypes()}
                            onChange={(option: OptionType) => formik.setFieldValue("race", option.value)}
                            value={formik.values.race}
                            placeholder={`${intl.formatMessage(LANG_COMMON_CHOOSE)}`}
                            isSearchable={false}
                            isTouched={formik.touched.race}
                            isValid={formik.touched.race && !formik.errors.race}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group className="mb-3">
                        <Form.Label>{intl.formatMessage(LANG_PERSON_HEIGHT)} <span className="text-danger">*</span></Form.Label>
                        <InputMask
                            mask="9.99"
                            value={formik.values.height}
                            onChange={(e) => {
                                formik.setFieldValue("height", e.target.value);
                                updateImc(e.target.value, formik.values.weight);
                            }}
                            className={clsx("form-control", {
                                "is-invalid": formik.touched.height && formik.errors.height,
                                "is-valid": !formik.errors.height && formik.touched.height
                            })}
                        />
                    </Form.Group>
                </Col>
                <Col>
                    <Form.Group className="mb-3">
                        <Form.Label>{intl.formatMessage(LANG_PERSON_WEIGHT)} <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="number"
                            step=".5"
                            name="weight"
                            placeholder={`${intl.formatMessage(LANG_PERSON_WEIGHT)}`}
                            value={formik.values.weight}
                            onChange={(e) => {
                                formik.handleChange(e);
                                updateImc(formik.values.height_number, e.target.value);
                            }}
                            isValid={formik.touched.weight && !formik.errors.weight}
                            isInvalid={!!formik.errors.weight && formik.touched.weight}
                        /> 
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col lg={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>{intl.formatMessage(LANG_PERSON_BMI)}</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={`${intl.formatMessage(LANG_PERSON_BMI)}`}
                            value={imc}
                            disabled={true}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Group className="mb-3">
                        <Form.Label>{intl.formatMessage(LANG_PERSON_SKIN_COLOR)} <span className="text-danger">*</span></Form.Label>
                        <CustomSelect
                            name="skin_color"
                            options={options.personOptions?.getSkinColors()}
                            onChange={(option: OptionType) => formik.setFieldValue("skin_color", option.value)}
                            value={formik.values.skin_color}
                            placeholder={`${intl.formatMessage(LANG_COMMON_CHOOSE)}`}
                            isSearchable={false}
                            isTouched={formik.touched.skin_color}
                            isValid={formik.touched.skin_color && !formik.errors.skin_color}
                        />
                    </Form.Group>
                </Col>
                <Col>
                    <Form.Group className="mb-3">
                        <Form.Label>{intl.formatMessage(LANG_PERSON_HAIR_COLOR)} <span className="text-danger">*</span></Form.Label>
                        <CustomSelect
                            name="hair_color"
                            options={options.personOptions?.getHairColors()}
                            onChange={(option: OptionType) => formik.setFieldValue("hair_color", option.value)}
                            value={formik.values.hair_color}
                            placeholder={`${intl.formatMessage(LANG_COMMON_CHOOSE)}`}
                            isSearchable={false}
                            isTouched={formik.touched.hair_color}
                            isValid={formik.touched.hair_color && !formik.errors.hair_color}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col lg={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>{intl.formatMessage(LANG_PERSON_HAIR_TYPE)} <span className="text-danger">*</span></Form.Label>
                        <CustomSelect
                            name="hair_type"
                            options={options.personOptions?.getHairTypes()}
                            onChange={(option: OptionType) => formik.setFieldValue("hair_type", option.value)}
                            value={formik.values.hair_type}
                            placeholder={`${intl.formatMessage(LANG_COMMON_CHOOSE)}`}
                            isSearchable={false}
                            isTouched={formik.touched.hair_type}
                            isValid={formik.touched.hair_type && !formik.errors.hair_type}
                        />
                    </Form.Group>
                </Col>
                <Col>
                    <Form.Group className="mb-3">
                        <Form.Label>{intl.formatMessage(LANG_PERSON_EYES_COLOR)} <span className="text-danger">*</span></Form.Label>
                        <CustomSelect
                            name="eyes_color"
                            options={options.personOptions?.getEyesColors()}
                            onChange={(option: OptionType) => formik.setFieldValue("eyes_color", option.value)}
                            value={formik.values.eyes_color}
                            placeholder={`${intl.formatMessage(LANG_COMMON_CHOOSE)}`}
                            isSearchable={false}
                            isTouched={formik.touched.eyes_color}
                            isValid={formik.touched.eyes_color && !formik.errors.eyes_color}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row className={"mb-3"}>
                <div className="col-lg-6 fv-row">
                    <Form.Group className="mb-3">
                        <Form.Label>{intl.formatMessage(LANG_COMMON_ZIPCODE)} <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="text"
                            name="zipcode"
                            placeholder={`${intl.formatMessage(LANG_COMMON_ZIPCODE)}`}
                            value={formik.values.zipcode}
                            onChange={formik.handleChange}
                            isValid={formik.touched.zipcode && !formik.errors.zipcode}
                            isInvalid={!!formik.errors.zipcode && formik.touched.zipcode}
                        />
                    </Form.Group>
                </div>
            </Row>
            <Row>
                <Col lg={12}>
                    <Form.Label>
                        <FormattedMessage
                            {...LANG_PERSON_OTHER_INFO}
                        />
                        <small>
                            {" "}
                            <FormattedMessage
                                {...LANG_PERSON_OTHER_INFO_DETAILS}
                            />
                        </small></Form.Label>
                    <textarea name="notes" rows={5} className="form-control" onChange={formik.handleChange} value={formik.values.notes} />
                </Col>
            </Row>
        </>
    );
}

export default PersonPhenotypeForm;